import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { COUNTRY_CENTROIDS } from "@/data/countryCentroids";
import { Eye, Users as UsersIcon, LogIn, Fingerprint, Globe, Download, Flame, MessageSquare, Check, RotateCcw, Trash2, Mail } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, sub, tone = "blue", testid }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <Card data-testid={testid} className="border-slate-200 p-5 shadow-none">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</span>
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${tones[tone]}`}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>
      <div className="mt-3 font-mono text-3xl font-light tracking-tight text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </Card>
  );
};

const Breakdown = ({ title, items, testid }) => {
  const total = items.reduce((a, b) => a + b.count, 0) || 1;
  return (
    <Card className="border-slate-200 p-5 shadow-none" data-testid={testid}>
      <h3 className="mb-4 text-sm font-semibold text-slate-800">{title}</h3>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-slate-400">—</p>}
        {items.map((it) => (
          <div key={it.name}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="text-slate-600">{it.name}</span>
              <span className="font-mono text-slate-400">{it.count} ({Math.round((it.count / total) * 100)}%)</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-slate-100">
              <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${(it.count / total) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const CountryMap = ({ countries, t }) => {
  const mapped = (countries || []).filter((c) => COUNTRY_CENTROIDS[c.code]);
  const max = mapped.reduce((m, c) => Math.max(m, c.count), 0) || 1;
  const radius = (n) => 6 + (n / max) * 22;
  return (
    <Card className="border-slate-200 shadow-none" data-testid="country-card">
      <div className="border-b border-slate-100 p-4">
        <h3 className="text-sm font-semibold text-slate-800">{t("analytics.byCountry")}</h3>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3">
        <div className="overflow-hidden lg:col-span-2" data-testid="country-map">
          <MapContainer center={[25, 5]} zoom={1} minZoom={1} worldCopyJump
            style={{ height: "360px", width: "100%", background: "#eaf2fb" }} scrollWheelZoom={false}>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {mapped.map((c) => (
              <CircleMarker key={c.code} center={COUNTRY_CENTROIDS[c.code]} radius={radius(c.count)}
                pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.45, weight: 1.5 }}>
                <LeafletTooltip>{c.name}: {c.count} {t("analytics.visits").toLowerCase()}</LeafletTooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="max-h-[360px] overflow-y-auto border-t border-slate-100 lg:border-l lg:border-t-0">
          {(countries || []).length === 0 && (
            <p className="p-4 text-sm text-slate-400">{t("analytics.noVisits")}</p>
          )}
          {(countries || []).map((c) => (
            <div key={c.code || c.name} data-testid={`country-row-${c.code || "unk"}`}
              className="flex items-center justify-between border-b border-slate-50 px-4 py-2.5 text-sm last:border-0">
              <span className="text-slate-700">{c.name}</span>
              <span className="font-mono text-slate-500">{c.count}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default function Analytics() {
  const { t } = useI18n();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [logins, setLogins] = useState(null);
  const [downloads, setDownloads] = useState(null);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dlGroup, setDlGroup] = useState("all");
  const [contactSearch, setContactSearch] = useState("");
  const [contactStatus, setContactStatus] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const [clientStatus, setClientStatus] = useState("all");

  const load = useCallback(async () => {
    const [o, l, d, ld, c] = await Promise.all([
      api.get(`/analytics/overview?days=${days}`),
      api.get(`/analytics/logins?days=${days}`),
      api.get(`/downloads?days=${days}`),
      api.get(`/leads`),
      api.get(`/contacts`),
    ]);
    setOverview(o.data);
    setLogins(l.data);
    setDownloads(d.data);
    setLeads(ld.data);
    setContacts(c.data);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : t("users.never"));
  const fmtDay = (d) => new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });

  const INTEREST_LABELS = { info: t("lead.interestInfo"), dosaloggers: t("lead.interestDosaloggers"), ambas: t("lead.interestBoth") };
  const dlEvents = (downloads?.events || []).filter((e) => dlGroup === "all" || e.group === dlGroup);
  const dlByDay = (() => {
    const map = {};
    dlEvents.forEach((e) => {
      const day = (e.timestamp || "").slice(0, 10);
      if (day) map[day] = (map[day] || 0) + 1;
    });
    return Object.keys(map).sort().map((day) => ({ date: day, count: map[day] }));
  })();
  const dlTopProducts = (() => {
    const map = {};
    dlEvents.forEach((e) => { const k = e.product_name || "?"; map[k] = (map[k] || 0) + 1; });
    return Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 5);
  })();

  const exportCsv = () => {
    const cols = ["timestamp", "name", "email", "company", "country", "phone", "sector", "interests", "group", "product_name", "manual_title"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = cols.join(",");
    const rows = dlEvents.map((e) => cols.map((c) => esc(c === "interests" ? (INTEREST_LABELS[e[c]] || e[c]) : e[c])).join(","));
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dosacloud-descargas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pendingContacts = contacts.filter((c) => !c.attended).length;
  const hotLeadsCount = leads.filter((l) => l.hot).length;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const downloadsThisMonth = (downloads?.events || []).filter((e) => (e.timestamp || "").slice(0, 7) === thisMonth).length;
  const toggleAttend = async (c) => {
    try {
      await api.put(`/contacts/${c.id}/attend`, { attended: !c.attended });
      setContacts((prev) => prev.map((x) => x.id === c.id ? { ...x, attended: !c.attended } : x));
    } catch { toast.error("Error"); }
  };
  const deleteContact = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      setContacts((prev) => prev.filter((x) => x.id !== id));
    } catch { toast.error("Error"); }
  };
  const filteredContacts = [
    ...contacts.map((c) => ({ ...c, _type: "contact", _date: c.timestamp })),
    ...leads.map((l) => ({
      id: l.id, _type: "client", name: l.name, email: l.email, company: l.company,
      message: `${l.interests ? INTEREST_LABELS[l.interests] : "—"} · ${l.download_count} ${t("analytics.downloadsCol").toLowerCase()}`,
      attended: l.followup_status === "contacted", followup_status: l.followup_status,
      lang: "", timestamp: l.created_at, _date: l.created_at,
    })),
  ]
    .sort((a, b) => (b._date || "").localeCompare(a._date || ""))
    .filter((c) => {
      if (contactStatus === "pending" && c.attended) return false;
      if (contactStatus === "attended" && !c.attended) return false;
      const term = contactSearch.trim().toLowerCase();
      if (!term) return true;
      return (c.company || "").toLowerCase().includes(term) || (c.email || "").toLowerCase().includes(term) || (c.name || "").toLowerCase().includes(term);
    });
  const exportContactsCsv = () => {
    const cols = ["timestamp", "name", "email", "company", "lang", "attended", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredContacts.map((c) => cols.map((k) => esc(k === "attended" ? (c.attended ? "1" : "0") : c[k])).join(","));
    const csv = "\uFEFF" + [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dosacloud-contactos-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleFollowup = async (l) => {
    const next = l.followup_status === "contacted" ? "pending" : "contacted";
    try {
      await api.put(`/leads/${l.id}/followup`, { status: next });
      setLeads((prev) => prev.map((x) => x.id === l.id ? { ...x, followup_status: next } : x));
    } catch { toast.error("Error"); }
  };
  const filteredClients = leads.filter((l) => {
    if (clientStatus === "pending" && l.followup_status === "contacted") return false;
    if (clientStatus === "contacted" && l.followup_status !== "contacted") return false;
    const term = clientSearch.trim().toLowerCase();
    if (!term) return true;
    return (l.name || "").toLowerCase().includes(term) || (l.company || "").toLowerCase().includes(term) || (l.email || "").toLowerCase().includes(term);
  });
  const pendingClients = leads.filter((l) => l.followup_status !== "contacted").length;
  const pendingInbox = pendingContacts + pendingClients;
  const exportClientsCsv = () => {
    const cols = ["created_at", "name", "email", "company", "country", "phone", "sector", "interests", "email_verified", "download_count", "followup_status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredClients.map((l) => cols.map((k) =>
      esc(k === "interests" ? (INTEREST_LABELS[l[k]] || l[k]) : (k === "email_verified" ? (l[k] ? "1" : "0") : l[k]))).join(","));
    const csv = "\uFEFF" + [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dosacloud-clientes-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const rangeSelect = (
    <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
      <SelectTrigger className="w-40" data-testid="analytics-range"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="7">{t("analytics.last7")}</SelectItem>
        <SelectItem value="30">{t("analytics.last30")}</SelectItem>
        <SelectItem value="90">{t("analytics.last90")}</SelectItem>
        <SelectItem value="365">{t("analytics.last365")}</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <Layout>
      <PageHeader title={t("analytics.title")} subtitle={t("analytics.subtitle")} actions={rangeSelect} />
      <div className="space-y-6 p-6">
        <div data-testid="commercial-summary">
          <h2 className="text-sm font-semibold text-slate-800">{t("analytics.commercialSummary")}</h2>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard testid="kpi-hot-leads" icon={Flame} label={t("analytics.kpiHotLeads")} value={hotLeadsCount} tone="amber" />
            <KpiCard testid="kpi-downloads-month" icon={Download} label={t("analytics.kpiDownloadsMonth")} value={downloadsThisMonth} tone="blue" />
            <KpiCard testid="kpi-pending-contacts" icon={MessageSquare} label={t("analytics.kpiPendingContacts")} value={pendingContacts} tone="violet" />
          </div>
        </div>
        <Tabs defaultValue="traffic">
          <TabsList>
            <TabsTrigger value="traffic" data-testid="tab-traffic">{t("analytics.webTraffic")}</TabsTrigger>
            <TabsTrigger value="logins" data-testid="tab-logins">{t("analytics.userAccess")}</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-downloads">{t("analytics.downloads")}</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              {t("analytics.contacts")}
              {pendingInbox > 0 && (
                <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-semibold text-white" data-testid="contacts-pending-badge">{pendingInbox}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">
              {t("analytics.clients")}
              {pendingClients > 0 && (
                <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold text-white" data-testid="clients-pending-badge">{pendingClients}</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="traffic" className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiCard testid="kpi-visits" icon={Eye} label={t("analytics.totalVisits")}
                value={overview?.total_visits ?? "—"} sub={t("analytics.inPeriod")} tone="blue" />
              <KpiCard testid="kpi-unique" icon={Fingerprint} label={t("analytics.uniqueVisitors")}
                value={overview?.unique_visitors ?? "—"} sub={t("analytics.byIp")} tone="emerald" />
              <KpiCard testid="kpi-pages" icon={Globe} label={t("analytics.pagesTracked")}
                value={overview?.top_pages?.length ?? "—"} sub={t("analytics.distinctPages")} tone="violet" />
            </div>

            <Card className="border-slate-200 p-5 shadow-none">
              <h3 className="mb-4 text-sm font-semibold text-slate-800">{t("analytics.visitsByDay")}</h3>
              <div className="h-64" data-testid="visits-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview?.by_day || []} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={20} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} width={35} />
                    <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()}
                      formatter={(v) => [v, t("analytics.visits")]}
                      contentStyle={{ fontSize: 12, borderRadius: 8, background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))", color: "hsl(var(--popover-foreground))" }} />
                    <Bar dataKey="count" fill="#2563eb" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {(!overview || overview.total_visits === 0) && (
                <p className="mt-3 text-center text-sm text-slate-400" data-testid="no-visits">{t("analytics.noVisits")}</p>
              )}
            </Card>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Breakdown title={t("analytics.byDevice")} items={overview?.devices || []} testid="bk-devices" />
              <Breakdown title={t("analytics.byBrowser")} items={overview?.browsers || []} testid="bk-browsers" />
              <Breakdown title={t("analytics.bySystem")} items={overview?.os || []} testid="bk-os" />
            </div>

            <CountryMap countries={overview?.countries} t={t} />

            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("analytics.topPages")}</h3>
              </div>
              <Table data-testid="top-pages-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("analytics.page")}</TableHead>
                    <TableHead className="text-right">{t("analytics.visits")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(overview?.top_pages || []).map((p) => (
                    <TableRow key={p.path}>
                      <TableCell className="font-mono text-xs text-slate-600">{p.path}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{p.count}</TableCell>
                    </TableRow>
                  ))}
                  {(overview?.top_pages || []).length === 0 && (
                    <TableRow><TableCell colSpan={2} className="text-center text-sm text-slate-400">{t("analytics.noVisits")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="logins" className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <KpiCard testid="kpi-logins" icon={LogIn} label={t("analytics.totalLogins")}
                value={logins?.total_logins ?? "—"} sub={t("analytics.inPeriod")} tone="blue" />
              <KpiCard testid="kpi-activeusers" icon={UsersIcon} label={t("analytics.activeUsers")}
                value={logins?.unique_users ?? "—"} sub={t("analytics.whoLoggedIn")} tone="emerald" />
            </div>

            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("analytics.accessByUser")}</h3>
              </div>
              <Table data-testid="logins-by-user-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("common.role")}</TableHead>
                    <TableHead className="text-right">{t("analytics.accessCount")}</TableHead>
                    <TableHead>{t("analytics.lastAccess")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(logins?.by_user || []).map((u) => (
                    <TableRow key={u.email}>
                      <TableCell className="text-sm font-medium text-slate-800">{u.name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{u.email}</TableCell>
                      <TableCell className="text-sm capitalize text-slate-500">{u.role}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{u.count}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{fmt(u.last)}</TableCell>
                    </TableRow>
                  ))}
                  {(logins?.by_user || []).length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400">{t("analytics.noLogins")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("analytics.recentAccess")}</h3>
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                <Table data-testid="logins-recent-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("analytics.date")}</TableHead>
                      <TableHead>{t("common.email")}</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>{t("analytics.device")}</TableHead>
                      <TableHead>{t("analytics.browser")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(logins?.recent || []).map((e, i) => (
                      <TableRow key={`${e.timestamp}-${i}`}>
                        <TableCell className="font-mono text-xs text-slate-500">{fmt(e.timestamp)}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{e.email}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{e.ip}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.device}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.browser}</TableCell>
                      </TableRow>
                    ))}
                    {(logins?.recent || []).length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400">{t("analytics.noLogins")}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="downloads" className="space-y-6">
            <div className="flex items-center justify-between gap-3">
              <Select value={dlGroup} onValueChange={setDlGroup}>
                <SelectTrigger className="w-56" data-testid="downloads-group-filter"><SelectValue placeholder={t("analytics.filterGroup")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("analytics.allGroups")}</SelectItem>
                  <SelectItem value="controladores">{t("products.group.controladores")}</SelectItem>
                  <SelectItem value="sondas">{t("products.group.sondas")}</SelectItem>
                  <SelectItem value="bombas">{t("products.group.bombas")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={exportCsv} disabled={dlEvents.length === 0} data-testid="downloads-export-csv">
                <Download className="mr-2 h-4 w-4" /> {t("analytics.exportCsv")}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <KpiCard testid="kpi-downloads" icon={Download} label={t("analytics.totalDownloads")}
                value={dlEvents.length} sub={t("analytics.inPeriod")} tone="amber" />
              <KpiCard testid="kpi-dl-products" icon={Globe} label={t("analytics.topProductsDl")}
                value={dlTopProducts.length} sub={t("analytics.distinctPages")} tone="violet" />
            </div>

            <Card className="border-slate-200 p-5 shadow-none">
              <h3 className="mb-4 text-sm font-semibold text-slate-800">{t("analytics.downloadsByDay")}</h3>
              <div className="h-64" data-testid="downloads-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dlByDay} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={20} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} width={35} />
                    <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()}
                      formatter={(v) => [v, t("analytics.downloads")]}
                      contentStyle={{ fontSize: 12, borderRadius: 8, background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))", color: "hsl(var(--popover-foreground))" }} />
                    <Bar dataKey="count" fill="#f59e0b" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {dlEvents.length === 0 && (
                <p className="mt-3 text-center text-sm text-slate-400" data-testid="no-downloads">{t("analytics.noDownloads")}</p>
              )}
            </Card>

            <Card className="border-slate-200 shadow-none" data-testid="hot-leads-card">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("analytics.hotLeads")}</h3>
                <p className="mt-0.5 text-xs text-slate-400">{t("analytics.hotLeadsSub")}</p>
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                <Table data-testid="hot-leads-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("analytics.lead")}</TableHead>
                      <TableHead>{t("common.email")}</TableHead>
                      <TableHead>{t("lead.company")}</TableHead>
                      <TableHead>{t("analytics.interest")}</TableHead>
                      <TableHead className="text-right">{t("analytics.downloadsCol")}</TableHead>
                      <TableHead>{t("analytics.lastDownload")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.filter((l) => l.download_count > 0).map((l) => (
                      <TableRow key={l.id} data-testid={`hot-lead-${l.id}`} className={l.hot ? "bg-orange-50/50" : ""}>
                        <TableCell className="text-sm font-medium text-slate-800">
                          <span className="inline-flex items-center gap-1.5">
                            {l.hot && <Flame className="h-4 w-4 text-orange-500" />}
                            {l.name || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{l.email}</TableCell>
                        <TableCell className="text-sm text-slate-600">{l.company || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-500">{l.interests ? INTEREST_LABELS[l.interests] : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-slate-700">{l.download_count}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{fmt(l.last_download)}</TableCell>
                      </TableRow>
                    ))}
                    {leads.filter((l) => l.download_count > 0).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400">{t("analytics.noLeads")}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("analytics.recentDownloads")}</h3>
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                <Table data-testid="downloads-recent-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("analytics.date")}</TableHead>
                      <TableHead>{t("lead.company")}</TableHead>
                      <TableHead>{t("common.email")}</TableHead>
                      <TableHead>{t("analytics.interest")}</TableHead>
                      <TableHead>{t("analytics.product")}</TableHead>
                      <TableHead>{t("analytics.manual")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dlEvents.slice(0, 200).map((e, i) => (
                      <TableRow key={`${e.timestamp}-${i}`}>
                        <TableCell className="font-mono text-xs text-slate-500">{fmt(e.timestamp)}</TableCell>
                        <TableCell className="text-sm text-slate-700">{e.company || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{e.email}</TableCell>
                        <TableCell className="text-xs text-slate-500">{INTEREST_LABELS[e.interests] || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.product_name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.manual_title}</TableCell>
                      </TableRow>
                    ))}
                    {dlEvents.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400">{t("analytics.noDownloads")}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="contacts" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={contactStatus} onValueChange={setContactStatus}>
                  <SelectTrigger className="w-40" data-testid="contacts-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("analytics.filterAll")}</SelectItem>
                    <SelectItem value="pending">{t("analytics.filterPending")}</SelectItem>
                    <SelectItem value="attended">{t("analytics.filterAttended")}</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)}
                  placeholder={t("analytics.searchContacts")} className="w-64" data-testid="contacts-search" />
              </div>
              <Button variant="outline" onClick={exportContactsCsv} disabled={filteredContacts.length === 0} data-testid="contacts-export-csv">
                <Download className="mr-2 h-4 w-4" /> {t("analytics.exportCsv")}
              </Button>
            </div>
            <Card className="border-slate-200 shadow-none">
              <div className="flex items-center justify-between border-b border-slate-100 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{t("analytics.contacts")}</h3>
                  <p className="mt-0.5 text-xs text-slate-400">{t("analytics.contactsSub")}</p>
                </div>
                {pendingInbox > 0 && (
                  <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{pendingInbox} {t("analytics.pendingCount")}</Badge>
                )}
              </div>
              <div className="max-h-[560px] overflow-y-auto">
                <Table data-testid="contacts-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("analytics.date")}</TableHead>
                      <TableHead>{t("analytics.type")}</TableHead>
                      <TableHead>{t("common.name")}</TableHead>
                      <TableHead>{t("common.email")}</TableHead>
                      <TableHead>{t("lead.company")}</TableHead>
                      <TableHead>{t("analytics.message")}</TableHead>
                      <TableHead>{t("analytics.attended")}</TableHead>
                      <TableHead className="text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredContacts.map((c) => (
                      <TableRow key={`${c._type}-${c.id}`} data-testid={`contact-row-${c.id}`} className={!c.attended ? "bg-orange-50/40" : ""}>
                        <TableCell className="whitespace-nowrap font-mono text-xs text-slate-500">{fmt(c.timestamp)}</TableCell>
                        <TableCell>
                          {c._type === "client"
                            ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{t("analytics.typeClient")}</Badge>
                            : <Badge variant="outline" className="text-slate-500">{t("analytics.typeContact")}</Badge>}
                        </TableCell>
                        <TableCell className="text-sm font-medium text-slate-800">{c.name}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{c.email}</TableCell>
                        <TableCell className="text-sm text-slate-600">{c.company || "—"}</TableCell>
                        <TableCell className="max-w-xs text-sm text-slate-600"><span className="line-clamp-2" title={c.message}>{c.message}</span></TableCell>
                        <TableCell>
                          {c.attended
                            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{c._type === "client" ? t("analytics.contacted") : t("analytics.attended")}</Badge>
                            : <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{t("analytics.pending")}</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button asChild variant="outline" size="sm" data-testid={`contact-reply-${c.id}`}>
                              <a href={`mailto:${c.email}?subject=${encodeURIComponent("Re: Dosacloud")}&body=${encodeURIComponent((c.name ? "Hola " + c.name + ",\n\n" : "") )}`}>
                                <Mail className="h-3.5 w-3.5" />
                                <span className="ml-1.5 hidden lg:inline">{t("analytics.reply")}</span>
                              </a>
                            </Button>
                            <Button variant="outline" size="sm"
                              onClick={() => c._type === "client" ? toggleFollowup(c) : toggleAttend(c)}
                              data-testid={`contact-attend-${c.id}`}>
                              {c.attended ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                              <span className="ml-1.5 hidden sm:inline">
                                {c._type === "client"
                                  ? (c.attended ? t("analytics.markPendingFollow") : t("analytics.markContacted"))
                                  : (c.attended ? t("analytics.markPending") : t("analytics.markAttended"))}
                              </span>
                            </Button>
                            {c._type === "contact" && (
                              <Button variant="ghost" size="sm" onClick={() => deleteContact(c.id)} data-testid={`contact-delete-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredContacts.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-slate-400" data-testid="no-contacts">
                        <MessageSquare className="mx-auto mb-2 h-6 w-6 text-slate-300" />{t("analytics.noContacts")}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="clients" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={clientStatus} onValueChange={setClientStatus}>
                  <SelectTrigger className="w-44" data-testid="clients-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("analytics.filterAll")}</SelectItem>
                    <SelectItem value="pending">{t("analytics.pendingFollow")}</SelectItem>
                    <SelectItem value="contacted">{t("analytics.contacted")}</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
                  placeholder={t("analytics.searchClients")} className="w-64" data-testid="clients-search" />
              </div>
              <Button variant="outline" onClick={exportClientsCsv} disabled={filteredClients.length === 0} data-testid="clients-export-csv">
                <Download className="mr-2 h-4 w-4" /> {t("analytics.exportCsv")}
              </Button>
            </div>
            <Card className="border-slate-200 shadow-none">
              <div className="flex items-center justify-between border-b border-slate-100 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{t("analytics.clients")}</h3>
                  <p className="mt-0.5 text-xs text-slate-400">{t("analytics.clientsSub")}</p>
                </div>
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{leads.length} {t("analytics.clientsCount").toLowerCase()}</Badge>
              </div>
              <div className="max-h-[560px] overflow-y-auto">
                <Table data-testid="clients-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("analytics.registered")}</TableHead>
                      <TableHead>{t("common.name")}</TableHead>
                      <TableHead>{t("common.email")}</TableHead>
                      <TableHead>{t("lead.company")}</TableHead>
                      <TableHead>{t("lead.country")}</TableHead>
                      <TableHead>{t("lead.phone")}</TableHead>
                      <TableHead>{t("lead.sector")}</TableHead>
                      <TableHead>{t("analytics.interest")}</TableHead>
                      <TableHead className="text-right">{t("analytics.downloadsCol")}</TableHead>
                      <TableHead>{t("analytics.followup")}</TableHead>
                      <TableHead className="text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((l) => (
                      <TableRow key={l.id} data-testid={`client-row-${l.id}`} className={l.followup_status !== "contacted" ? "bg-blue-50/30" : ""}>
                        <TableCell className="whitespace-nowrap font-mono text-xs text-slate-500">{fmt(l.created_at)}</TableCell>
                        <TableCell className="text-sm font-medium text-slate-800">
                          <span className="inline-flex items-center gap-1.5">
                            {l.hot && <Flame className="h-4 w-4 text-orange-500" />}
                            {l.name || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{l.email}</TableCell>
                        <TableCell className="text-sm text-slate-600">{l.company || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{l.country || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{l.phone || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-500">{l.sector || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-500">{l.interests ? INTEREST_LABELS[l.interests] : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-slate-700">{l.download_count}</TableCell>
                        <TableCell>
                          {l.followup_status === "contacted"
                            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("analytics.contacted")}</Badge>
                            : <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{t("analytics.pendingFollow")}</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button asChild variant="outline" size="sm" data-testid={`client-reply-${l.id}`}>
                              <a href={`mailto:${l.email}?subject=${encodeURIComponent("Dosatronic")}&body=${encodeURIComponent((l.name ? "Hola " + l.name + ",\n\n" : ""))}`}>
                                <Mail className="h-3.5 w-3.5" />
                                <span className="ml-1.5 hidden lg:inline">{t("analytics.reply")}</span>
                              </a>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => toggleFollowup(l)} data-testid={`client-followup-${l.id}`}>
                              {l.followup_status === "contacted" ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                              <span className="ml-1.5 hidden sm:inline">{l.followup_status === "contacted" ? t("analytics.markPendingFollow") : t("analytics.markContacted")}</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredClients.length === 0 && (
                      <TableRow><TableCell colSpan={11} className="py-8 text-center text-sm text-slate-400" data-testid="no-clients">
                        <UsersIcon className="mx-auto mb-2 h-6 w-6 text-slate-300" />{t("analytics.noClientsYet")}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </Layout>
  );
}
